import React from "react";

import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme, darken } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { interpolateDivergant, toIndex, toPercentage } from "@/utils";

interface VariableCardProps {
  name: string;
  value: number;
  score: string;
  minValue?: number;
  maxValue?: number;
}

const VariableCard: React.FC<VariableCardProps> = ({
  name,
  value,
  score,
  minValue,
  maxValue,
}) => {
  const theme = useTheme();

  const formatFunc = score === "rate" ? toPercentage : toIndex;

  const calcPercentage = (value: number) => {
    const mx = maxValue ? maxValue : 1;
    const mn = minValue ? minValue : 0;
    return Math.max(0.01, (value - mn) / (mx - mn));
  };

  return (
    <Grid key={name}>
      <Card
        sx={{
          border: 1,
          borderRadius: 1,
          borderColor: theme.palette.divider,
          width: "230px",
        }}
      >
        <CardContent
          sx={{
            pb: 2,
            "&:last-child": { pb: 2 },
          }}
        >
          <Stack spacing={0}>
            <HelpBox title={name} placement="top">
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "1",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {name}
              </Typography>
            </HelpBox>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                height="4px"
                borderRadius="4px"
                width={`${calcPercentage(value) * 100}%`}
                sx={{
                  backgroundColor: interpolateDivergant(
                    theme.palette.score.light,
                    theme.palette.score.main,
                    theme.palette.score.dark,
                    1 - calcPercentage(value),
                  ),
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: interpolateDivergant(
                    theme.palette.score.light,
                    theme.palette.score.main,
                    theme.palette.score.dark,
                    1 - calcPercentage(value),
                  ),
                }}
              >
                {formatFunc(value)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default VariableCard;
